import GetAppIcon from '@mui/icons-material/GetApp';
import { Box, Typography, TypographyProps } from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import {
  GridCsvGetRowsToExportParams,
  GridExcelExportOptions,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  gridSortedRowIdsSelector,
  useGridApiContext
} from '@mui/x-data-grid-premium';
import { ContextExportData, PageContextExporter } from 'components/page-context/ExportPageContext';
import { IsSuperAdmin } from 'shared/permissions/IsSuperAdmin';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import toast from 'shared/utils/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) =>
  gridSortedRowIdsSelector(apiRef);

export const exportButtonProps: ButtonProps = {
  variant: 'text',
  size: 'medium',
  startIcon: <GetAppIcon />,
  className: 'normal-case font-normal',
  sx: {
    '& .MuiButton-startIcon': {
      color: '#006DB0'
    }
  }
};
export const buttonTypography: TypographyProps = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '1.2', // 120%
  letterSpacing: '0em',
  textTransform: 'none',
  color: '#006DB0'
};

interface EnhancedToolbarProps {
  /** Core orchestration service managing backend data models and exports */
  service: any;
  /** Unique identification tracker key for contextual item uploads */
  entityId?: number | null;
  /** Parameter object mapped directly from parent layout criteria states */
  outerParams?: Record<string, unknown>;
  /** Switch revoking baseline item mutation features */
  disableEdit?: boolean;
  /** Authorization switch overriding and revoking data layout migration options */
  disableExport?: boolean;
}

/**
 * Presentation-agnostic default utility toolbar designed for MUI DataGrid Premium setups.
 * Incorporates columns visibility toggles, Excel row exporters, and role-protected backend pipelines.
 *
 * Remember to update : miniava
 */
export const EnhancedToolbar = ({
  service,
  entityId,
  outerParams,
  disableExport
}: EnhancedToolbarProps) => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();

  const exporterRef = useRef<any>(null);
  const [markdownContext, setMarkdownContext] = useState<string>('');

  const handleExport = (options: GridExcelExportOptions) =>
    apiRef.current.exportDataAsExcel(options);

  const exportAll = () => {
    if (outerParams) service.exportAll(outerParams);
    else service.exportAll();
    toast.info(t('messages.export-required'));
  };

  const exportSummary = () => {
    if (outerParams) service.exportSummary(outerParams);
    else service.exportSummary();
    toast.info(t('messages.export-required'));
  };

  // Dispatch firmato basato sull'URL corrente con gestione del ciclo di pulizia
  useEffect(() => {
    if (markdownContext) {
      window.dispatchEvent(
        new CustomEvent('ava-context-update', {
          detail: {
            text: markdownContext,
            url: window.location.pathname // Firma l'evento con il path reale attuale
          }
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('ava-context-update', {
          detail: { text: '', url: window.location.pathname }
        })
      );
    }

    // CLEANUP AUTOMATICO: Se la tabella si smonta, notifica il reset per questo URL
    return () => {
      window.dispatchEvent(
        new CustomEvent('ava-context-update', {
          detail: { text: '', url: window.location.pathname }
        })
      );
    };
  }, [markdownContext]);

  // Callback per intercettare i cambi di contesto della griglia
  const handleContextChange = useCallback(
    (context: ContextExportData) => {
      if (exporterRef.current) {
        const newMarkdown = exporterRef.current.exportContext();
        setMarkdownContext(newMarkdown || '');
      }
    },
    [exporterRef]
  );

  // Funzione di debug/test per il markdown
  // const testMarkdown = () => {
  //   if (exporterRef.current) {
  //     alert(exporterRef.current.exportContext());
  //   }
  // };

  return (
    <GridToolbarContainer className="flex justify-between items-center w-full p-2">
      {/* Sinistra: Colonne e Filtri */}
      <Box
        className="flex gap-2"
        sx={{
          padding: '4px',
          '& .MuiButton-root': {
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '1.2',
            letterSpacing: '0em',
            textTransform: 'none',
            color: '#006DB0'
          },

          '& .MuiButton-startIcon': {
            color: '#006DB0'
          }
        }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </Box>
      {/* Destra: Azioni di Export e Import */}
      <div className="flex gap-2 items-center">
        <Button
          {...exportButtonProps}
          onClick={() => handleExport({ getRowsToExport: getFilteredRows })}>
          <Typography {...buttonTypography}>{t('data-grid.download-filtered-rows')}</Typography>
        </Button>

        <IsTenantAdministrator>
          {!!service.exportAll && !disableExport && (
            <Button {...exportButtonProps} onClick={exportAll} color="success">
              {t('data-grid.export-all')}
            </Button>
          )}
          {!!service.exportSummary && (
            <Button {...exportButtonProps} onClick={exportSummary} color="success">
              <Typography {...buttonTypography}>{t('data-grid.export-summary')}</Typography>
            </Button>
          )}
        </IsTenantAdministrator>

        <IsSuperAdmin>
          {(!!service.importFromFile || !!service.importFromFileDetail) && (
            <ImportFileUploader service={service} entityId={entityId} />
          )}
          {/* <Button
            {...exportButtonProps}
            onClick={testMarkdown}
            sx={{ ...exportButtonProps.sx, textTransform: 'none' }}>
            <Typography {...buttonTypography}>Download Markdown Test</Typography>
          </Button> */}
        </IsSuperAdmin>
      </div>
      <PageContextExporter
        apiRef={apiRef}
        ref={exporterRef}
        onContextChange={handleContextChange}
      />
    </GridToolbarContainer>
  );
};
